import { z } from "zod";

import { MAX_LENGTH_MESSAGE, MIN_LENGTH_MESSAGE, REQUIRED_MESSAGE } from "@/utils";

import { EProductType } from "../products.type";

export const creditModalityEnum = z.enum([
  "consignado",
  "pessoal",
  "flexpay",
  "fgts_antecipacao",
  "private_label",
  "home_equity",
  "auto_equity",
  "financiamento_veiculo",
  "agricola",
]);

export const creditConsignadoScopeEnum = z.enum(["publico", "privado"]);

export const creditInterestRateBasisEnum = z.enum(["with_insurance", "without_insurance"]);

export const creditDaysBasisEnum = z.enum([
  "business_days",
  "calendar_days",
  "within_1h",
  "immediate",
]);

export const creditRemunerationTypeEnum = z.enum([
  "percent_on_released",
  "fixed_per_released",
  "fixed_per_lead",
]);

export const creditTargetPersonEnum = z.enum(["individual", "company"]);

export const creditProductConfigSchema = z
  .object({
    supplier_name: z
      .string()
      .max(200, { message: MAX_LENGTH_MESSAGE("200") })
      .optional(),
    modality: creditModalityEnum,
    consignado_scope: creditConsignadoScopeEnum.optional(),
    public_sector_category: z.string().optional(),
    interest_rate_basis: creditInterestRateBasisEnum,
    interest_rate_percent: z.coerce.number().min(0).max(100),
    has_value_premise: z.boolean(),
    min_released_value: z.coerce.number().min(0).optional(),
    max_released_value: z.coerce.number().min(0).optional(),
    release_deadline_basis: creditDaysBasisEnum,
    release_deadline_days: z.coerce.number().int().min(0),
    remuneration_type: creditRemunerationTypeEnum,
    remuneration_value: z.coerce.number().min(0),
    allows_negative_credit: z.boolean(),
    has_age_restriction: z.boolean(),
    min_age_years: z.coerce.number().int().min(0).max(150).optional(),
    max_age_years: z.coerce.number().int().min(0).max(150).optional(),
    target_person_type: creditTargetPersonEnum,
    notes: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.modality === "consignado" && !data.consignado_scope) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["consignado_scope"],
      });
    }
    if (
      data.modality === "consignado" &&
      data.consignado_scope === "publico" &&
      (!data.public_sector_category || data.public_sector_category.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["public_sector_category"],
      });
    }
    if (data.has_value_premise) {
      if (data.min_released_value === undefined || data.max_released_value === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["min_released_value"],
        });
      } else if (data.max_released_value < data.min_released_value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "products.credit.validation.maxReleasedGteMin",
          path: ["max_released_value"],
        });
      }
    }
    if (data.has_age_restriction) {
      if (data.min_age_years === undefined || data.max_age_years === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["min_age_years"],
        });
      } else if (data.max_age_years < data.min_age_years) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "products.credit.validation.maxAgeGteMin",
          path: ["max_age_years"],
        });
      }
    }
  });

export const creditProductFormSchema = z.object({
  type: z.literal(EProductType.CREDIT),
  name: z
    .string()
    .min(2, { message: MIN_LENGTH_MESSAGE("2") })
    .max(150, { message: MAX_LENGTH_MESSAGE("150") }),
  insurance_company_id: z.preprocess(
    (v) => (v === "" || v === null || v === undefined ? undefined : v),
    z.string().uuid().optional(),
  ),
  infinity_sync_as_source: z.boolean().optional(),
  external_product_id: z
    .string()
    .max(255, { message: MAX_LENGTH_MESSAGE("255") })
    .optional()
    .nullable(),
  credit_product_config: creditProductConfigSchema,
});

export type TCreditProductFormData = z.infer<typeof creditProductFormSchema>;

export type TCreditProductConfigForm = z.infer<typeof creditProductConfigSchema>;

export function getDefaultCreditProductConfig(): TCreditProductConfigForm {
  return {
    modality: "pessoal",
    interest_rate_basis: "without_insurance",
    interest_rate_percent: 0,
    has_value_premise: false,
    release_deadline_basis: "business_days",
    release_deadline_days: 0,
    remuneration_type: "percent_on_released",
    remuneration_value: 0,
    allows_negative_credit: false,
    has_age_restriction: false,
    target_person_type: "individual",
  };
}

export function mergeCreditProductConfigFromApi(
  raw: unknown,
): TCreditProductConfigForm {
  const defaults = getDefaultCreditProductConfig();
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return defaults;
  }
  const r = raw as Record<string, unknown>;
  const {
    estimated_release_basis: _e1,
    estimated_release_days: _e2,
    ...rest
  } = r;
  const merged = { ...defaults, ...rest };
  const parsed = creditProductConfigSchema.safeParse(merged);
  return parsed.success ? parsed.data : defaults;
}

export function getDefaultCreditProductForm(): TCreditProductFormData {
  return {
    type: EProductType.CREDIT,
    name: "",
    infinity_sync_as_source: true,
    external_product_id: "",
    credit_product_config: getDefaultCreditProductConfig(),
  };
}
